import { OrderStatusId } from "../Domain/Order";
import { UseCaseResult } from "../TypesAndInterfaces/UseCaseResult";
import { coachContactInfo, getCustomerInfoReadByKeyUseCase } from "../UseCases/getCustomerInfoReadByKey";
import { ordersCountByPartnerResult, ordersCountByPartnersKeyUseCase } from "../UseCases/ordersCountByPartnersKey";
import { readOrdersByParamsUseCase } from "../UseCases/readOrdersByParams";
import { lastPageByStatusResult, setLastPageUseCase } from "../UseCases/setLastPage";

export interface OrderState {
  initComplete: boolean;
  orders: [];
  pageActive: number;
  pageSize: number;
  currentStatusId?: string;
  currentOrdersPage?: any;
  order: {};
  orderStatus: {};
  coachContactInfo: {};
  ordersCount: {};
  loading: boolean;
  lastPageByStatus: {};
  continuationTokens: {
    [statusId: string]: {
      [pageNumber: number]: string | null;
    };
  };
  hasMoreResults: boolean;
}

const defaultOrderReducerData: OrderState = {
  initComplete: false,
  orders: [],
  pageActive: 1,
  pageSize: 10,
  currentStatusId: OrderStatusId.PendingReview,
  currentOrdersPage: undefined,
  order: {},
  orderStatus: {},
  coachContactInfo: {},
  ordersCount: undefined,
  loading: false,
  lastPageByStatus: {},
  continuationTokens: {},
  hasMoreResults: false
};

export const OrderReducer = function (state: OrderState = { ...defaultOrderReducerData }, useCaseResult: UseCaseResult) {
  state = Object.assign(state, {
    actionResult: useCaseResult
  });

  switch (useCaseResult.type) {
    case readOrdersByParamsUseCase.type:
      {
        const result = useCaseResult.data;

        if (useCaseResult.start()) {
          return {
            ...state,
            loading: true
          };
        }

        if (useCaseResult.success()) {
          const { continuationToken, hasMoreResults } = result;
          const currentStatusId = result.statusId || state.currentStatusId;
          const currentPage = result.page ;

          const updatedContinuationTokens = {
            ...state.continuationTokens,
            [currentStatusId]: {
              ...(state.continuationTokens[currentStatusId] || {}),
              [currentPage]: continuationToken
            }
          };

          return {
            ...state,
            orders: typeof result.orders === 'string' ? JSON.parse(result.orders) : result.orders,
            currentStatusId: currentStatusId,
            continuationTokens: updatedContinuationTokens,
            hasMoreResults: hasMoreResults,
            loading: false
          };
        }

        if (useCaseResult.failure()) {
          return {
            ...state,
            loading: false
          };
        }

        return state;
      }

    case getCustomerInfoReadByKeyUseCase.type:
      const customerInfo: coachContactInfo = useCaseResult.data;

      if (useCaseResult.success()) {
        return Object.assign({}, state, {
          coachContactInfo: customerInfo,
        });
      }
      break;

    case ordersCountByPartnersKeyUseCase.type:
      const result: ordersCountByPartnerResult = useCaseResult.data;
      const items = result && groupStatuses(result.ordersCountByPartner);

      if (useCaseResult.success()) {
        return Object.assign({}, state, {
          ordersCount: items
        });
      }
      break;

    case setLastPageUseCase.type:
      const data: lastPageByStatusResult = useCaseResult.data;

      if (useCaseResult.success()) {
        return Object.assign({}, state, {
          lastPageByStatus: data
        });
      }
      break;

    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        pageActive: useCaseResult.data.pageNumber
      };

    case 'RESET_PAGINATION':
      const statusId = useCaseResult.data?.statusId;
      if (statusId) {
        const { [statusId]: removed, ...remainingTokens } = state.continuationTokens;
        return {
          ...state,
          continuationTokens: remainingTokens,
          pageActive: 1,
          hasMoreResults: false
        };
      } else {
        return {
          ...state,
          continuationTokens: {},
          pageActive: 1,
          hasMoreResults: false
        };
      }

    default:
      return state;
  }

  return state;
};

function groupStatuses(data: any) {
  let sentForManufacturingCount = 0;

  data?.forEach(item => {
    if (item.orderStatusId === OrderStatusId.SentForManufacturing || item.orderStatusId === OrderStatusId.StandBy) {
      sentForManufacturingCount += item.orders_count;
    }
  });

  data?.forEach(item => {
    if (item.orderStatusId === OrderStatusId.SentForManufacturing) {
      item.orders_count = sentForManufacturingCount;
    }
  });

  return data;
}

export const getContinuationToken = (state: OrderState, statusId: string, pageNumber: number): string | null => {
  return state.continuationTokens[statusId]?.[pageNumber] || null;
};

export const hasMoreResultsForPage = (state: OrderState, statusId: string, pageNumber: number): boolean => {
  return !!state.continuationTokens[statusId]?.[pageNumber + 1];
};

export const clearContinuationTokens = (state: OrderState, statusId?: string): OrderState => {
  if (statusId) {
    const { [statusId]: removed, ...remainingTokens } = state.continuationTokens;
    return {
      ...state,
      continuationTokens: remainingTokens,
      pageActive: 1,
      hasMoreResults: false
    };
  } else {
    return {
      ...state,
      continuationTokens: {},
      pageActive: 1,
      hasMoreResults: false
    };
  }
};